import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import axios from 'src/utils/axios';
import { Button } from "src/@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/@/components/ui/select";
import { Card, CardHeader, CardContent, CardTitle } from "src/@/components/ui/card";
import { Label } from "src/@/components/ui/label";
import Iconify from 'src/components/Iconify';
import { PopoverVariaveis } from "../components/PopoverVariaveis";
import VariableInput from "../components/VariableInput";
import FlowStateBuilder from "../components/FlowStateBuilder";

const HttpRequestComponent = ({ block, changeBlock, availableVariables = [] }) => {
  const { t } = useTranslation('automation');
  const [headers, setHeaders] = useState(block?.data?.httpHeaders || [{ key: "", value: "" }]);
  const [params, setParams] = useState(block?.data?.httpParams || [{ key: "", value: "" }]);
  const [url, setUrl] = useState(block?.data?.httpUrl || '');
  const [body, setBody] = useState(block?.data?.httpBody || '');
  const [flowState, setFlowState] = useState(block?.data?.flowState || []);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Sincronizar estado local com block quando ele mudar
  useEffect(() => {
    setUrl(block?.data?.httpUrl || '');
    setBody(block?.data?.httpBody || '');
    setHeaders(block?.data?.httpHeaders || [{ key: "", value: "" }]);
    setParams(block?.data?.httpParams || [{ key: "", value: "" }]);
    setFlowState(block?.data?.flowState || []);
  }, [block?.id]);

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setHeaders(newHeaders);
    changeBlock({ name: "httpHeaders", value: newHeaders });
  };

  const addHeader = () => {
    const newHeaders = [...headers, { key: "", value: "" }];
    setHeaders(newHeaders);
    changeBlock({ name: "httpHeaders", value: newHeaders });
  };

  const removeHeader = (index) => {
    const newHeaders = headers.filter((_, i) => i !== index);
    setHeaders(newHeaders);
    changeBlock({ name: "httpHeaders", value: newHeaders });
  };

  // Params handlers
  const handleParamChange = (index, field, value) => {
    const newParams = [...params];
    newParams[index] = { ...newParams[index], [field]: value };
    setParams(newParams);
    changeBlock({ name: "httpParams", value: newParams });
  };

  const addParam = () => {
    const newParams = [...params, { key: "", value: "" }];
    setParams(newParams);
    changeBlock({ name: "httpParams", value: newParams });
  };

  const removeParam = (index) => {
    const newParams = params.filter((_, i) => i !== index);
    setParams(newParams);
    changeBlock({ name: "httpParams", value: newParams });
  };

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    changeBlock({ name: "httpUrl", value: newUrl });
  };

  const handleBodyChange = (e) => {
    const newBody = e.target.value;
    setBody(newBody);
    changeBlock({ name: "httpBody", value: newBody });
  };

  const handleAddVariableToUrl = (variable) => {
    const variableText = `{{${variable}}}`;
    const newUrl = url + variableText;
    setUrl(newUrl);
    changeBlock({ name: "httpUrl", value: newUrl });
  };

  const handleAddVariableToBody = (variable) => {
    const variableText = `{{${variable}}}`;
    const newBody = body + variableText;
    setBody(newBody);
    changeBlock({ name: "httpBody", value: newBody });
  };

  // Calcular URL completa com parâmetros
  const fullUrl = useMemo(() => {
    if (!url) return '';

    const validParams = params.filter(p => p.key && p.key.trim() !== '');
    if (validParams.length === 0) return url;

    const queryString = validParams
      .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value || '')}`)
      .join('&');

    // Verifica se a URL já tem query params
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${queryString}`;
  }, [url, params]);

  const currentNodeVariables = [
    { key: 'output', type: 'NODE OUTPUTS', description: t('httpRequest.outputs.response', 'Resposta da requisição (JSON)') },
    { key: 'status', type: 'NODE OUTPUTS', description: t('httpRequest.outputs.status', 'Status code da requisição') },
  ];

  // Função para testar a requisição via proxy do backend
  const handleTestRequest = async () => {
    if (!url) {
      setTestResult({
        error: true,
        message: t('httpRequest.errors.urlRequired', 'URL é obrigatória para testar a requisição')
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const method = block?.data?.httpMethod || 'GET';

      // Chamar o endpoint de proxy do backend
      const response = await axios.post('/api/v1/proxy/test-http', {
        url,
        method,
        headers,
        params,
        body
      });

      const result = response.data;

      if (result.error) {
        setTestResult({
          error: true,
          message: result.message,
          details: result.details
        });
      } else {
        setTestResult({
          success: true,
          status: result.status,
          statusText: result.statusText,
          duration: result.duration,
          headers: result.headers,
          data: result.data
        });
      }
    } catch (error) {
      setTestResult({
        error: true,
        message: error.response?.data?.message || error.message || t('httpRequest.errors.executionError', 'Erro ao executar requisição'),
        details: error.toString()
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="w-full border-0 shadow-none">
      <CardHeader className="px-0 pt-0 pb-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Iconify icon="mdi:api" className="text-orange-600" />
          {t('httpRequest.title', 'Requisição HTTP')}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 space-y-4">
        {/* URL */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-semibold text-gray-500">URL</Label>
            <PopoverVariaveis
              onSelectVariavel={handleAddVariableToUrl}
              variables={availableVariables.map(v => ({ name: v.key, desc: v.description }))}
            >
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-orange-600 hover:text-orange-700 hover:bg-orange-50">
                <Iconify icon="mdi:variable" className="mr-1" width={14} />
                {t('httpRequest.variablesButton', 'Variáveis')}
              </Button>
            </PopoverVariaveis>
          </div>
          <VariableInput
            value={url}
            onChange={handleUrlChange}
            placeholder={t('httpRequest.url.placeholder', 'https://api.exemplo.com/endpoint')}
            className="bg-white text-sm"
            variables={availableVariables}
          />
          {/* URL Preview com parâmetros */}
          {fullUrl && params.some(p => p.key && p.key.trim() !== '') && (
            <div className="mt-2 p-2 bg-slate-50 rounded-md border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <Iconify icon="mdi:link-variant" className="text-slate-400" width={14} />
                <span className="text-[10px] font-semibold text-slate-500 uppercase">{t('httpRequest.url.fullUrlLabel', 'URL Completa')}</span>
              </div>
              <div className="text-xs font-mono text-slate-700 break-all bg-white p-2 rounded border border-slate-100">
                {fullUrl}
              </div>
            </div>
          )}
        </div>

        {/* Método */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-gray-500">{t('httpRequest.method.label', 'Método')}</Label>
          <Select
            value={block?.data?.httpMethod || 'GET'}
            onValueChange={(value) => changeBlock({ name: "httpMethod", value })}
          >
            <SelectTrigger className="h-9 bg-white w-full">
              <SelectValue placeholder={t('httpRequest.method.placeholder', 'Selecione o método')} />
            </SelectTrigger>
            <SelectContent>
              {["GET", "POST", "PUT", "PATCH", "DELETE"].map((method) => (
                <SelectItem key={method} value={method}>
                  <span className="flex items-center gap-2">
                    <span className={`text-xs font-mono font-bold ${method === 'GET' ? 'text-green-600' :
                      method === 'POST' ? 'text-blue-600' :
                        method === 'PUT' ? 'text-yellow-600' :
                          method === 'PATCH' ? 'text-purple-600' :
                            'text-red-600'
                      }`}>
                      {method}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Query Parameters */}
        <div className="space-y-3 pt-2 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-semibold text-gray-700">{t('httpRequest.params.label', 'Parâmetros (Query Params)')}</Label>
              <Iconify icon="mdi:information-outline" className="text-gray-400" width={16} />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addParam}
              className="h-8 text-xs border-dashed border-gray-400 text-gray-600 hover:bg-gray-50"
            >
              <Iconify icon="mdi:plus" className="mr-1" width={14} />
              {t('httpRequest.params.addButton', 'Adicionar Parâmetro')}
            </Button>
          </div>

          <div className="space-y-2">
            {params.map((param, index) => (
              <Card key={index} className="p-3 bg-blue-50 border border-blue-200 relative">
                <div className="absolute top-2 right-2 flex items-center gap-2">
                  <span className="bg-blue-200 text-blue-600 text-[10px] px-1.5 rounded-sm font-mono">{index}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-gray-400 hover:text-red-500 hover:bg-red-50"
                    onClick={() => removeParam(index)}
                  >
                    <Iconify icon="mdi:trash-can-outline" width={14} />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">{t('httpRequest.common.keyLabel', 'Chave')}</Label>
                    <VariableInput
                      value={param.key}
                      onChange={(e) => handleParamChange(index, 'key', e.target.value)}
                      className="h-8 bg-white"
                      placeholder="page"
                      variables={availableVariables}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">{t('labels.value', { ns: 'common', defaultValue: 'Valor' })}</Label>
                    <VariableInput
                      value={param.value}
                      onChange={(e) => handleParamChange(index, 'value', e.target.value)}
                      className="h-8 bg-white"
                      placeholder="1"
                      variables={availableVariables}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <p className="text-[10px] text-gray-400">
            {t('httpRequest.params.hint', 'Os parâmetros serão adicionados à URL como query string (ex: ?page=1&limit=10)')}
          </p>
        </div>

        {/* Headers */}
        <div className="space-y-3 pt-2 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-semibold text-gray-700">{t('httpRequest.headers.label', 'Cabeçalhos (Headers)')}</Label>
              <Iconify icon="mdi:information-outline" className="text-gray-400" width={16} />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addHeader}
              className="h-8 text-xs border-dashed border-gray-400 text-gray-600 hover:bg-gray-50"
            >
              <Iconify icon="mdi:plus" className="mr-1" width={14} />
              {t('httpRequest.headers.addButton', 'Adicionar Header')}
            </Button>
          </div>

          <div className="space-y-2">
            {headers.map((header, index) => (
              <Card key={index} className="p-3 bg-gray-50 border border-gray-200 relative">
                <div className="absolute top-2 right-2 flex items-center gap-2">
                  <span className="bg-gray-200 text-gray-600 text-[10px] px-1.5 rounded-sm font-mono">{index}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-gray-400 hover:text-red-500 hover:bg-red-50"
                    onClick={() => removeHeader(index)}
                  >
                    <Iconify icon="mdi:trash-can-outline" width={14} />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">{t('httpRequest.common.keyLabel', 'Chave')}</Label>
                    <VariableInput
                      value={header.key}
                      onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                      className="h-8 bg-white"
                      placeholder="Content-Type"
                      variables={availableVariables}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">{t('labels.value', { ns: 'common', defaultValue: 'Valor' })}</Label>
                    <VariableInput
                      value={header.value}
                      onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                      className="h-8 bg-white"
                      placeholder="application/json"
                      variables={availableVariables}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="space-y-2 pt-2 border-t">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-semibold text-gray-500">{t('httpRequest.body.label', 'Body (Corpo da Requisição)')}</Label>
            <PopoverVariaveis
              onSelectVariavel={handleAddVariableToBody}
              variables={availableVariables.map(v => ({ name: v.key, desc: v.description }))}
            >
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-orange-600 hover:text-orange-700 hover:bg-orange-50">
                <Iconify icon="mdi:variable" className="mr-1" width={14} />
                {t('httpRequest.variablesButton', 'Variáveis')}
              </Button>
            </PopoverVariaveis>
          </div>
          <VariableInput
            multiline
            value={body}
            onChange={handleBodyChange}
            className="min-h-[120px] resize-y bg-white text-sm font-mono"
            placeholder={t('httpRequest.body.placeholder', '{"key": "value", "nome": "{{variableExample}}"}', { variableExample: '{{nome}}' })}
            variables={availableVariables}
          />
          <p className="text-[10px] text-gray-400">
            {t('httpRequest.body.hint', 'Use variáveis para incluir informações dinâmicas no corpo da requisição.')}
          </p>
        </div>

        {/* Botão de Teste */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-semibold text-gray-700">{t('httpRequest.test.label', 'Testar Requisição')}</Label>
              <Iconify icon="mdi:flask-outline" className="text-gray-400" width={16} />
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleTestRequest}
              disabled={testing || !url}
              className="h-8 text-xs bg-orange-600 hover:bg-orange-700 text-white"
            >
              {testing ? (
                <>
                  <Iconify icon="mdi:loading" className="mr-1 animate-spin" width={14} />
                  {t('httpRequest.test.testing', 'Testando...')}
                </>
              ) : (
                <>
                  <Iconify icon="mdi:play" className="mr-1" width={14} />
                  {t('httpRequest.test.executeButton', 'Executar Teste')}
                </>
              )}
            </Button>
          </div>
          <p className="text-[10px] text-gray-400">
            {t('httpRequest.test.hint', 'Execute um teste da requisição para verificar se está funcionando. Variáveis (ex: {{variableExample}}) não serão substituídas no teste.', { variableExample: '{{nome}}' })}
          </p>

          {/* Resultado do Teste */}
          {testResult && (
            <Card className={`p-3 ${testResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <div className="space-y-3">
                {/* Header do resultado */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Iconify
                      icon={testResult.error ? "mdi:alert-circle" : "mdi:check-circle"}
                      className={testResult.error ? "text-red-500" : "text-green-500"}
                      width={20}
                    />
                    <span className={`text-sm font-semibold ${testResult.error ? 'text-red-700' : 'text-green-700'}`}>
                      {testResult.error ? t('httpRequest.result.errorTitle', 'Erro na Requisição') : t('httpRequest.result.successTitle', 'Requisição Bem-sucedida')}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-gray-400 hover:text-gray-600"
                    onClick={() => setTestResult(null)}
                  >
                    <Iconify icon="mdi:close" width={14} />
                  </Button>
                </div>

                {testResult.error ? (
                  <div className="space-y-2">
                    <div className="text-sm text-red-600 font-medium">{testResult.message}</div>
                    {testResult.details && (
                      <div className="text-xs text-red-500 font-mono bg-red-100 p-2 rounded">
                        {testResult.details}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Status e Tempo */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{t('httpRequest.result.statusLabel', 'Status:')}</span>
                        <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${
                          testResult.status >= 200 && testResult.status < 300 ? 'bg-green-200 text-green-700' :
                          testResult.status >= 400 ? 'bg-red-200 text-red-700' :
                          'bg-yellow-200 text-yellow-700'
                        }`}>
                          {testResult.status} {testResult.statusText}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{t('httpRequest.result.durationLabel', 'Tempo:')}</span>
                        <span className="text-xs font-mono bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          {testResult.duration}ms
                        </span>
                      </div>
                    </div>

                    {/* Headers da Resposta (colapsável) */}
                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
                        {t('httpRequest.result.responseHeadersLabel', 'Headers da Resposta')}
                      </summary>
                      <div className="mt-2 bg-gray-100 p-2 rounded font-mono text-[10px] max-h-32 overflow-auto">
                        {Object.entries(testResult.headers || {}).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="text-purple-600">{key}:</span>
                            <span className="text-gray-700">{value}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Body da Resposta */}
                    <div className="space-y-1">
                      <span className="text-xs text-gray-500 font-medium">{t('httpRequest.result.responseBodyLabel', 'Resposta:')}</span>
                      <div className="bg-white border border-gray-200 rounded p-2 max-h-64 overflow-auto">
                        <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-all">
                          {typeof testResult.data === 'object'
                            ? JSON.stringify(testResult.data, null, 2)
                            : testResult.data}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <FlowStateBuilder
          flowState={flowState}
          onChange={(newStates) => {
            setFlowState(newStates);
            changeBlock({ name: 'flowState', value: newStates });
          }}
          availableVariables={availableVariables}
          currentNodeVariables={currentNodeVariables}
        />
      </CardContent>
    </Card>
  );
};

export default HttpRequestComponent;
